/**
 * Transak Widget Component
 *
 * Handles the Transak fiat on-ramp / off-ramp integration.
 *
 * Responsibilities:
 *  - Read widget pre-fill config from data-* attributes (set by PHP/CMS)
 *  - On page load: POST to WP REST endpoint which builds the widget URL
 *    server-side (API key never exposed to the browser)
 *  - Dynamically import the Transak SDK and embed the widget inline
 *  - Emit dataLayer events for GTM tracking
 *
 * Data flow:
 *   [data-transak-widget] attrs → _readConfig()
 *   → POST /wp-json/bespoke/v1/transak/session
 *   → widgetUrl → Transak SDK (embedded in [data-transak-container])
 *
 * @see inc/transak-api.php                       (WP REST endpoint)
 * @see classes/Blocks/TransakBlock.php           (ACF block registration)
 * @see template-parts/blocks/transak/transak.php (template / data-* attrs)
 * @see https://docs.transak.com/integration/web/js-sdk
 */

/** WP REST endpoint that builds the widget URL server-side. */
const SESSION_ENDPOINT = '/wp-json/bespoke/v1/transak/session';

/** Cloudflare Turnstile script — loads on demand via injectTurnstileScript(). */
const TURNSTILE_SCRIPT_URL    = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_SCRIPT_TIMEOUT = 4000;  // give up if the CDN never responds
const TURNSTILE_TOKEN_TIMEOUT  = 2500;  // give up if the challenge never completes

/** Cached promise so multiple widget instances on a page share one script load. */
let turnstileScriptPromise = null;

/**
 * Stuck-widget watchdog: if TRANSAK_WIDGET_INITIALISED hasn't fired within this
 * window after we hand the widgetUrl to the SDK, treat the widget as stuck
 * (Transak refusing to serve — VPN/geo-block — or a silent iframe failure) and
 * emit `transak_widget_stuck`. Chosen below the 15s spinner-hide so we signal
 * BEFORE the spinner disappears (a hidden spinner + no widget looked like success).
 */
const STUCK_TIMEOUT_MS = 12000;

/**
 * Diagnostic postMessage tap. When enabled, we listen for messages from Transak
 * origins and record the message TYPE keys only (never payloads/PII) so we can
 * learn what Transak posts in the blocked/"wrap" case. Disable by setting
 * window.cgTransakDiag = false before this module runs.
 */
const TRANSAK_ORIGINS = [
    'https://global.transak.com',
    'https://global-stg.transak.com',
];

/**
 * Inject the Turnstile script tag once and resolve when window.turnstile is ready.
 * Rejects if the script fails to load (ad-blocker, DNS, etc.) or if it never
 * initialises within the timeout — callers should handle by surfacing an error
 * rather than calling /session unverified.
 */
/**
 * Resolve when window.turnstile is ready.
 *
 * @param {boolean} awaitExternal - When true, another plugin (e.g. CF7) will load
 *   the Turnstile script — poll for window.turnstile instead of injecting our own.
 *   Injecting a second script alongside CF7's causes CF internal error 600010.
 */
function injectTurnstileScript(awaitExternal = false) {
    if (turnstileScriptPromise) return turnstileScriptPromise;

    // Reuse any Turnstile script already in the DOM.
    const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');

    if (existingScript) {
        if (window.turnstile) return Promise.resolve();

        turnstileScriptPromise = new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                turnstileScriptPromise = null;
                reject(new Error('Turnstile script load timed out'));
            }, TURNSTILE_SCRIPT_TIMEOUT);

            existingScript.addEventListener('load', () => {
                clearTimeout(timeoutId);
                if (window.turnstile) {
                    resolve();
                } else {
                    turnstileScriptPromise = null;
                    reject(new Error('Turnstile global missing after load'));
                }
            }, { once: true });

            existingScript.addEventListener('error', () => {
                clearTimeout(timeoutId);
                turnstileScriptPromise = null;
                reject(new Error('Turnstile script failed to load (network or ad-blocker)'));
            }, { once: true });
        });

        return turnstileScriptPromise;
    }

    // PHP detected another plugin will load Turnstile (e.g. CF7 loads it in the
    // footer after our code runs). Poll for window.turnstile — never inject our
    // own copy, as two simultaneous Turnstile scripts cause CF 600010.
    if (awaitExternal) {
        turnstileScriptPromise = new Promise((resolve, reject) => {
            if (window.turnstile) return resolve();

            const timeoutId = setTimeout(() => {
                clearInterval(intervalId);
                turnstileScriptPromise = null;
                reject(new Error('Turnstile script load timed out'));
            }, TURNSTILE_SCRIPT_TIMEOUT);

            const intervalId = setInterval(() => {
                if (window.turnstile) {
                    clearInterval(intervalId);
                    clearTimeout(timeoutId);
                    resolve();
                }
            }, 50);
        });

        return turnstileScriptPromise;
    }

    // No Turnstile script in DOM and none expected — inject ours with explicit render mode.
    turnstileScriptPromise = new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            turnstileScriptPromise = null;
            reject(new Error('Turnstile script load timed out'));
        }, TURNSTILE_SCRIPT_TIMEOUT);

        const script = document.createElement('script');
        script.src   = TURNSTILE_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            clearTimeout(timeoutId);
            if (window.turnstile) {
                resolve();
            } else {
                turnstileScriptPromise = null;
                reject(new Error('Turnstile global missing after load'));
            }
        };
        script.onerror = () => {
            clearTimeout(timeoutId);
            turnstileScriptPromise = null;
            reject(new Error('Turnstile script failed to load (network or ad-blocker)'));
        };
        document.head.appendChild(script);
    });

    return turnstileScriptPromise;
}

export default class TransakWidget {

    /**
     * @param {HTMLElement} el - The `[data-transak-widget]` root element.
     */
    constructor(el) {
        this.el            = el;
        this.container     = el.querySelector('[data-transak-container]');
        this.loading       = el.querySelector('[data-transak-loading]');
        this.error         = el.querySelector('[data-transak-error]');
        this.turnstileHost = el.querySelector('[data-transak-turnstile-host]');

        if (!this.container) return;

        // Empty string → Turnstile disabled (dev/staging or admin user).
        this.turnstileSiteKey        = el.dataset.turnstileSiteKey || '';
        this.turnstileAwaitExternal  = el.dataset.turnstileAwaitExternal === '1';
        this.wpNonce                 = el.dataset.wpNonce || '';
        this.buyCountry              = el.dataset.buyCountry || 'XX';

        this._stuckTimer   = null;   // watchdog handle, cleared on INITIALISED
        this._seenMsgTypes = null;   // Set of Transak postMessage type keys (diagnostic)

        this._abortCtrl = null;
        this._loaded    = false;
        this.config     = this._readConfig();

        // Start SDK download immediately — the widget always needs it. Kicking
        // it off here means the chunk downloads while Turnstile verifies, rather
        // than starting only after Turnstile resolves (saves the full verify time).
        const _sdkStart  = performance.now();
        this._sdkPromise = import('@transak/ui-js-sdk').then(m => {
            console.log(`[Transak timing]  SDK import:        ${Math.round(performance.now() - _sdkStart)}ms`);
            return m;
        });

        // Pre-warm Turnstile at page load — same pattern as _sdkPromise.
        // CF's fingerprint analysis runs while the user reads the page, so the
        // widget launches instantly on scroll. Disabled via TRANSAK_TURNSTILE_ENABLED
        // in PHP, which leaves data-turnstile-site-key empty → this returns null immediately.
        this._turnstilePromise = this._getTurnstileToken();

        this._observe();
    }

    // -------------------------------------------------------------------------
    // Visibility-gated loading
    // -------------------------------------------------------------------------

    /**
     * Defer all network activity until the block is near the viewport.
     * Uses IntersectionObserver with a generous rootMargin so the widget
     * starts loading ~400px before the user scrolls to it.
     */
    _observe() {
        if (!('IntersectionObserver' in window)) {
            // Fallback: load immediately on browsers without IO support.
            this._load();
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    observer.disconnect();
                    this._load();
                }
            },
            { rootMargin: '400px' }
        );

        observer.observe(this.el);
    }

    // -------------------------------------------------------------------------
    // Private methods
    // -------------------------------------------------------------------------

    /**
     * Read widget pre-fill config from data-* attributes.
     * All values are optional — Transak uses its own defaults when omitted.
     *
     * @returns {Object}
     */
    _readConfig() {
        const d      = this.el.dataset;
        const config = {};

        if (d.transakCrypto)       config.defaultCryptoCurrency = d.transakCrypto;
        if (d.transakFiatCurrency) config.defaultFiatCurrency   = d.transakFiatCurrency;
        if (d.transakFiatAmount)   config.defaultFiatAmount     = parseFloat(d.transakFiatAmount);
        if (d.transakProducts)     config.productsAvailed       = d.transakProducts;

        return config;
    }

    /**
     * Main flow: fetch a widget URL from the server, then embed the widget.
     * Called automatically on construction — no user interaction needed.
     */
    async _load() {
        if (this._loaded) return;   // Prevent duplicate loads.
        this._loaded = true;

        // Cancel any in-flight request from a prior attempt (e.g. rapid refresh).
        this._abortCtrl?.abort();
        this._abortCtrl = new AbortController();

        this._hideError();

        const _t0 = performance.now();
        console.group('[Transak timing] widget load started');

        try {
            // Turnstile and SDK import have no dependency on each other — run in
            // parallel. Session fetch needs the token, so it starts after both resolve.
            const _tParallel = performance.now();
            const [turnstileToken, { Transak }] = await Promise.all([
                this._turnstilePromise,
                this._sdkPromise,
            ]);
            console.log(`[Transak timing]  Turnstile + SDK:   ${Math.round(performance.now() - _tParallel)}ms (parallel wait)`);

            const _tSession = performance.now();
            const widgetUrl = await this._fetchSession(this._abortCtrl.signal, turnstileToken);
            console.log(`[Transak timing]  Session fetch:     ${Math.round(performance.now() - _tSession)}ms`);

            console.log(`[Transak timing]  TOTAL to launch:   ${Math.round(performance.now() - _t0)}ms`);
            console.groupEnd();

            this._launchWidget(widgetUrl, Transak);
        } catch (err) {
            console.log(`[Transak timing]  FAILED after:      ${Math.round(performance.now() - _t0)}ms`);
            console.groupEnd();
            if (err.name === 'AbortError') return;  // Superseded by a newer request.
            console.error('[Transak]', err);
            this._loaded = false;
            this._hideLoading();
            this._showError('Something went wrong loading the widget. Please refresh the page.');
            // Symmetric with Simplex's simplex_load_failed — feeds the shared
            // buy_widget_load_failed funnel step.
            window.dataLayer?.push({ event: 'transak_widget_load_failed' });
        }
    }

    /**
     * Run Cloudflare Turnstile and return the resulting token, or null when
     * the bot-check is disabled (dev/staging or logged-in admin).
     *
     * Tokens are single-use server-side; we render an invisible widget into
     * a throwaway off-screen container so multiple instances on a page can
     * each request their own token.
     *
     * @returns {Promise<string|null>}
     */
    async _getTurnstileToken() {
        if (!this.turnstileSiteKey) return null;

        try {
            return await this._getTurnstileTokenInner();
        } catch (err) {
            console.warn('[Transak] Turnstile unavailable, proceeding without token:', err.message);
            return null;
        }
    }

    async _getTurnstileTokenInner() {
        const _tScript = performance.now();
        await injectTurnstileScript(this.turnstileAwaitExternal);
        console.log(`[Transak timing]  Turnstile script:  ${Math.round(performance.now() - _tScript)}ms`);

        return new Promise((resolve, reject) => {
            let widgetId;
            let _resetCount  = 0;
            const _tChallenge = performance.now();

            const cleanup = () => {
                try { if (widgetId !== undefined) window.turnstile?.remove(widgetId); } catch (_) {}
            };

            const timeoutId = setTimeout(() => {
                cleanup();
                reject(new Error('Turnstile challenge timed out'));
            }, TURNSTILE_TOKEN_TIMEOUT);

            widgetId = window.turnstile.render(this.turnstileHost, {
                sitekey:  this.turnstileSiteKey,
                callback: (token) => {
                    console.log(`[Transak timing]  Turnstile token:  ${Math.round(performance.now() - _tChallenge)}ms${_resetCount ? ` (${_resetCount} × 600010 reset)` : ''}`);
                    clearTimeout(timeoutId);
                    cleanup();
                    resolve(token);
                },
                'error-callback': (errorCode) => {
                    // CF internal error on first-ever visit — no fingerprint yet.
                    // Reset the widget silently; Turnstile retries and usually succeeds.
                    if (String(errorCode) === '600010') {
                        // CF has no fingerprint yet for this visitor — resetting loops
                        // for 20+ seconds on mobile data. Fail-open immediately instead;
                        // the server applies a stricter rate limit on the no-token path.
                        console.log(`[Transak timing]  Turnstile 600010: fail-open at ${Math.round(performance.now() - _tChallenge)}ms`);
                        clearTimeout(timeoutId);
                        cleanup();
                        reject(new Error('Turnstile 600010 — proceeding without token'));
                        return;
                    }
                    clearTimeout(timeoutId);
                    cleanup();
                    reject(new Error(`Turnstile verification failed (code: ${errorCode})`));
                },
                'timeout-callback': () => {
                    clearTimeout(timeoutId);
                    cleanup();
                    reject(new Error('Turnstile challenge expired'));
                },
            });
        });
    }

    /**
     * POST to the WP REST endpoint which injects the API key server-side
     * and returns a ready-to-use widgetUrl.
     *
     * @returns {Promise<string>}
     */
    async _fetchSession(signal, turnstileToken) {
        const body = { ...this.config };
        if (turnstileToken) body.cfTurnstileToken = turnstileToken;

        const headers = { 'Content-Type': 'application/json' };
        if (this.wpNonce) headers['X-WP-Nonce'] = this.wpNonce;

        const response = await fetch(SESSION_ENDPOINT, {
            method:  'POST',
            headers,
            cache:   'no-store',   // widgetUrl is single-use — never cache this request
            body:    JSON.stringify(body),
            signal,
        });

        if (!response.ok) {
            // Try to surface the server-side error message for easier debugging.
            let detail = '';
            try { detail = (await response.json())?.message || ''; } catch (_) {}
            const message = response.status === 429
                ? 'Too many requests. Please wait a moment and refresh the page.'
                : `Session request failed (HTTP ${response.status})${detail ? ': ' + detail : ''}.`;
            throw new Error(message);
        }

        const data = await response.json();

        if (!data.widgetUrl) {
            throw new Error('Server response missing widgetUrl.');
        }

        return data.widgetUrl;
    }

    /**
     * Embed the widget inline inside [data-transak-container].
     *
     * @param {string} widgetUrl
     * @param {Function} Transak - The pre-loaded Transak SDK class.
     */
    _launchWidget(widgetUrl, Transak) {
        this._transak = new Transak({
            widgetUrl,
            containerId: this.container.id,
        });

        // --- GTM / analytics events ------------------------------------------
        // Each event pushes to window.dataLayer so GTM can pick it up without
        // requiring any GTM-side JS — just trigger on Custom Event.

        // Widget load time: from launch to the INITIALISED event.
        const loadStart = (window.performance && performance.now) ? performance.now() : null;

        // Start the diagnostic postMessage tap + stuck watchdog BEFORE init(), so
        // we capture whatever the widget posts (or its silence) from the very start.
        this._startDiagnosticTap();
        this._startStuckWatchdog(loadStart);

        Transak.on(Transak.EVENTS.TRANSAK_WIDGET_INITIALISED, () => {
            this._clearStuckWatchdog();   // widget is alive — cancel the stuck signal
            this._stopDiagnosticTap();    // no longer need the message tap
            this._hideLoading();
            if (loadStart !== null) {
                window.dataLayer?.push({
                    event:   'transak_widget_loaded',
                    load_ms: Math.round(performance.now() - loadStart),
                });
            }
            window.dataLayer?.push({ event: 'transak_widget_open' });
        });

        Transak.on(Transak.EVENTS.TRANSAK_ORDER_CREATED, (orderData) => {
            // Per Transak docs, ORDER_CREATED means KYC is complete and the order
            // is submitted (payment expected) — so it marks both funnel milestones.
            window.dataLayer?.push({ event: 'transak_kyc_completed' });
            window.dataLayer?.push({
                event:         'transak_order_created',
                transak_order: orderData?.data,
            });
        });

        Transak.on(Transak.EVENTS.TRANSAK_ORDER_SUCCESSFUL, (orderData) => {
            window.dataLayer?.push({
                event:         'transak_order_successful',
                transak_order: orderData?.data,
            });
        });

        Transak.on(Transak.EVENTS.TRANSAK_ORDER_FAILED, () => {
            window.dataLayer?.push({ event: 'transak_order_failed' });
        });

        Transak.on(Transak.EVENTS.TRANSAK_ORDER_CANCELLED, () => {
            window.dataLayer?.push({ event: 'transak_order_cancelled' });
        });

        Transak.on(Transak.EVENTS.TRANSAK_WIDGET_CLOSE, () => {
            window.dataLayer?.push({ event: 'transak_widget_close' });
        });

        // ---------------------------------------------------------------------
        this._transak.init();
    }

    // -------------------------------------------------------------------------
    // Stuck-widget watchdog + diagnostics
    // -------------------------------------------------------------------------

    /**
     * Arm a timer that fires `transak_widget_stuck` if the widget never reaches
     * TRANSAK_WIDGET_INITIALISED within STUCK_TIMEOUT_MS. This is the signal we
     * currently lack for the Transak VPN/geo-block ("wrap") case, where the
     * iframe loads but Transak silently refuses to serve. On fire we also hide
     * the spinner (previously the sole job of the old 15s timeout) so it never
     * permanently blocks the block.
     *
     * @param {number|null} loadStart - performance.now() at launch, for elapsed_ms.
     */
    _startStuckWatchdog(loadStart) {
        this._clearStuckWatchdog();
        this._stuckTimer = setTimeout(() => {
            this._stuckTimer = null;
            this._hideLoading();

            const props = {
                event:      'transak_widget_stuck',
                elapsed_ms: loadStart !== null ? Math.round(performance.now() - loadStart) : null,
                country:    this.buyCountry,
                page_path:  window.location.pathname,
            };
            // Attach the diagnostic message-type keys we saw (if any), so we can
            // learn the shape of the block signal. Types only — never payloads.
            if (this._seenMsgTypes && this._seenMsgTypes.size) {
                props.seen_message_types = Array.from(this._seenMsgTypes);
            }
            window.dataLayer?.push(props);
        }, STUCK_TIMEOUT_MS);
    }

    _clearStuckWatchdog() {
        if (this._stuckTimer !== null) {
            clearTimeout(this._stuckTimer);
            this._stuckTimer = null;
        }
    }

    /**
     * Listen for postMessages from Transak origins and record their TYPE keys
     * only (never payloads/PII). Purpose: discover what — if anything — Transak
     * posts when it refuses service, so the stuck event can carry that shape.
     * Opt-out via `window.cgTransakDiag = false`.
     */
    _startDiagnosticTap() {
        if (window.cgTransakDiag === false) return;
        this._seenMsgTypes = new Set();

        this._onMessage = (event) => {
            if (!TRANSAK_ORIGINS.includes(event.origin)) return;
            const d = event.data;
            // Record only a type/event identifier; ignore the rest of the payload.
            const type = (d && typeof d === 'object') ? (d.event_id || d.type || d.event) : null;
            if (type) this._seenMsgTypes.add(String(type));
        };
        window.addEventListener('message', this._onMessage);
    }

    _stopDiagnosticTap() {
        if (this._onMessage) {
            window.removeEventListener('message', this._onMessage);
            this._onMessage = null;
        }
    }

    // -------------------------------------------------------------------------
    // UI state helpers
    // -------------------------------------------------------------------------

    _hideLoading() {
        if (!this.loading) return;
        this.loading.classList.add('hidden');
    }

    _showError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.classList.remove('hidden');
    }

    _hideError() {
        if (!this.error) return;
        this.error.classList.add('hidden');
    }
}
